/**
 * Map tile layer definitions for Leaflet.
 * Provides Dark, Street, and Satellite views that work globally.
 */

export interface MapLayerDef {
  id: string;
  label: string;
  url: string;
  attribution: string;
  maxZoom: number;
}

export const MAP_LAYERS: MapLayerDef[] = [
  {
    id: "dark",
    label: "Dark",
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
    maxZoom: 19,
  },
  {
    id: "street",
    label: "Street",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  },
  {
    id: "satellite",
    label: "Satellite",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: '&copy; Esri, Maxar, Earthstar Geographics',
    maxZoom: 19,
  },
];

/**
 * Adds the layer toggle control to a Leaflet map.
 * Returns a cleanup function.
 */
export function addLayerToggle(L: any, map: any): () => void {
  const container = L.DomUtil.create("div", "leaflet-layer-toggle");
  container.style.cssText =
    "position:absolute;top:10px;right:10px;z-index:1000;display:flex;gap:0;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.3);";

  let currentTileLayer = L.tileLayer(MAP_LAYERS[0].url, {
    attribution: MAP_LAYERS[0].attribution,
    maxZoom: MAP_LAYERS[0].maxZoom,
  }).addTo(map);

  // Remove any existing tile layers first (the one added during map init)
  map.eachLayer((layer: any) => {
    if (layer !== currentTileLayer && layer._url && typeof layer._url === "string" && layer._url.includes("{z}")) {
      map.removeLayer(layer);
    }
  });

  const buttons: HTMLButtonElement[] = [];

  MAP_LAYERS.forEach((layerDef, idx) => {
    const btn = document.createElement("button");
    btn.textContent = layerDef.label;
    btn.style.cssText = `
      padding:6px 12px;font-size:11px;font-weight:600;border:none;cursor:pointer;
      font-family:inherit;transition:all 0.2s;letter-spacing:0.5px;
      ${idx === 0
        ? "background:rgba(240,180,41,0.9);color:#1a1a1a;"
        : "background:rgba(30,30,30,0.85);color:rgba(255,255,255,0.7);"}
    `;

    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      L.DomEvent.stopPropagation(e);

      map.removeLayer(currentTileLayer);
      currentTileLayer = L.tileLayer(layerDef.url, {
        attribution: layerDef.attribution,
        maxZoom: layerDef.maxZoom,
      }).addTo(map);
      // Send tile layer to back so markers stay on top
      currentTileLayer.bringToBack();

      buttons.forEach((b, i) => {
        if (i === idx) {
          b.style.background = "rgba(240,180,41,0.9)";
          b.style.color = "#1a1a1a";
        } else {
          b.style.background = "rgba(30,30,30,0.85)";
          b.style.color = "rgba(255,255,255,0.7)";
        }
      });
    });

    // Prevent map drag when clicking buttons
    L.DomEvent.disableClickPropagation(btn);
    buttons.push(btn);
    container.appendChild(btn);
  });

  map.getContainer().appendChild(container);

  return () => {
    if (container.parentNode) container.parentNode.removeChild(container);
  };
}
